import { tourSelector } from "training/anchors";
import { ITourContext, ITourStep, TourModule, TourRoute } from "training/types";

import { DriveStep } from "./useDriverTour";

/**
 * CLE-3015 — pure step-list helpers for the engine: which steps a run includes, how each maps onto a
 * driver.js step (spec §5 "driver.js mapping"), and where a step sits in its module.
 */

/** driver.js waitForElement for an anchored step without its own `waitMs` (spec §5: 8 s). */
export const DEFAULT_WAIT_MS = 8000;

/** driver.js waitForElement for the Cancel / No button inside the edit mode or dialog a click opened. */
export const FOLLOW_UP_WAIT_MS = 3000;

/** driver.js waitForElement for an item inside a menu the step before it opened. */
export const MENU_ITEM_WAIT_MS = 2000;

/** The class driver.js puts on every tour popover; the host's global style targets it. */
export const POPOVER_CLASS = "clear-certified-popover";
/** Added on a step with no element (card, picture): centred with room to spare, so it is wider (TourPopover). */
export const CARD_POPOVER_CLASS = "clear-certified-popover-card";

/**
 * The class the engine puts on <body> while a `fill` step shows (dev QA of #1438, F4). PrimeReact portals a
 * Dropdown's list, an AutoComplete's suggestions and a Calendar's pop-up to <body>, outside the highlighted
 * element: under driver.js's overlay (z-index 10000) and its `.driver-active * { pointer-events: none }` a
 * mouse could not pick from them. The host's global style lifts those panels above the overlay and the
 * popover, and lets them take clicks, only while this class is on.
 */
export const TOUR_PANELS_CLASS = "clear-certified-panels";

/** The line a gated step (`done`) shows under its text while Next is disabled, unless it sets `doneHint`. */
export const DONE_HINT = "Fill in the required fields to continue.";

/** How long the engine waits after an input / click / DOM change before re-checking a gate. */
export const DONE_DEBOUNCE_MS = 100;

/**
 * The gate is also re-checked on this poll: a value the page sets itself (the benefits the eligibility
 * check fills in) changes an input's `value` property, which fires no event and no DOM mutation.
 */
export const DONE_POLL_MS = 1000;

/**
 * Whether a step's gate is open now. No gate, no target on the page, or a check that throws → open: a gate
 * must never trap the trainee behind a disabled Next.
 */
export function isStepDone(step: ITourStep, doc: Document = document): boolean {
  if (!step.done || !step.anchor) return true;
  const target = doc.querySelector<HTMLElement>(tourSelector(step.anchor));
  if (!target) return true;
  try {
    return !!step.done(target);
  } catch (error) {
    console.error(`[Clear Certified] the gate of ${step.id} threw`, error);
    return true;
  }
}

/** Routes that show the trainee's own estimate: without it (no Create and Send yet) their steps drop out. */
export const ORDER_ROUTES: ReadonlyArray<TourRoute> = ["estimate", "patient"];

/**
 * One curriculum step → the steps the engine drives:
 * - `opener` → first a `click` on the menu trigger (`<id>-open`), so the element inside the menu exists;
 * - `open-cancel` → a `click` on the element, then a `click` on the follow-up's Cancel / No button
 *   (`<id>-cancel`). A click, not a look (PR 3): the edit mode or dialog the first click opened has to be
 *   closed before the tour goes on, and while a tour runs nothing outside the highlight can be clicked.
 */
export function expandStep(step: ITourStep): ITourStep[] {
  const before: ITourStep[] = step.opener
    ? [
        {
          id: `${step.id}-open`,
          module: step.module,
          countIn: step.countIn,
          anchor: step.opener.anchor,
          label: step.opener.label,
          mode: "click",
          route: step.route,
          text: step.opener.text,
        },
      ]
    : [];
  // A menu item exists only once the opener's click has opened the menu: it waits for it explicitly.
  const self: ITourStep = step.opener
    ? { ...step, opener: undefined, waitMs: step.waitMs ?? MENU_ITEM_WAIT_MS }
    : step;
  if (step.mode !== "open-cancel") return [...before, self];
  const opener: ITourStep = { ...self, mode: "click", followUp: undefined };
  if (!step.followUp) return [...before, opener];
  return [
    ...before,
    opener,
    {
      id: `${step.id}-cancel`,
      module: step.module,
      countIn: step.countIn,
      anchor: step.followUp.anchor,
      label: step.followUp.label,
      mode: "click",
      route: step.route,
      waitMs: FOLLOW_UP_WAIT_MS,
      text: step.followUp.text,
    },
  ];
}

/**
 * The steps this run includes: every step whose `when` holds for the context, with `opener` and
 * `open-cancel` expanded. A `when` that throws drops its step rather than the tour. A step on the trainee's
 * estimate (or its patient page) drops out while there is no estimate to show.
 */
export function buildVisibleSteps(
  steps: ITourStep[],
  ctx: ITourContext
): ITourStep[] {
  return steps
    .filter((step) => {
      if (ORDER_ROUTES.includes(step.route) && !ctx.order?.id) return false;
      if (!step.when) return true;
      try {
        return step.when(ctx);
      } catch {
        return false;
      }
    })
    .flatMap(expandStep);
}

/** How the step moves on: click on the element, a page event, or the popover's Next button. */
export function advanceKind(step: ITourStep): "click" | "event" | "next" {
  if (step.advanceOn === "event") return "event";
  if (
    step.advanceOn === "click" ||
    step.advanceOn === "route" ||
    step.mode === "click"
  ) {
    return "click";
  }
  return "next";
}

/**
 * The driver.js step for one curriculum step. The popover's content is React (TourPopover).
 *
 * `waitForTarget: false` (dev feedback round 1): an anchored step without its own `waitMs` gets no
 * `waitForElement`, so driver.js skips it the moment its target is not on the page (`skipMissingElement`)
 * instead of hiding the popover for DEFAULT_WAIT_MS first. A step with an explicit `waitMs` always waits.
 */
export function toDriveStep(
  step: ITourStep,
  { waitForTarget = true }: { waitForTarget?: boolean } = {}
): DriveStep {
  const anchored = !!step.anchor;
  const waits = step.waitMs !== undefined || waitForTarget;
  return {
    element: step.anchor ? tourSelector(step.anchor) : undefined,
    ...(anchored
      ? {
          ...(waits
            ? { waitForElement: step.waitMs ?? DEFAULT_WAIT_MS }
            : { waitForElement: 0 }),
          skipMissingElement: true,
        }
      : {}),
    // Never driver.js's: it listens in the bubble phase, which a page's stopPropagation() or a navigation
    // in the click's own handler defeats. The engine advances click steps itself (useTour `watchStep`).
    advanceOnClick: false,
    disableActiveInteraction: step.mode === "look",
    popover: {
      description: "",
      showButtons: [],
      // driver.js sets the wrapper's className to "driver-popover " + this, so two classes work.
      popoverClass: anchored
        ? POPOVER_CLASS
        : `${POPOVER_CLASS} ${CARD_POPOVER_CLASS}`,
    },
    data: { stepId: step.id },
  };
}

/**
 * Which steps of a segment wait for their target (DEFAULT_WAIT_MS unless they set `waitMs`): only where
 * the page may still be loading — the segment's first step, the step the segment starts on (a resume or a
 * hand-off lands mid-segment right after a navigation), and the first anchored step from there (when the
 * segment opens on a card, its first highlight is still close to the page load). Every other anchored step
 * is skipped at once when its target is not on the page; steps with an explicit `waitMs` always wait.
 */
export function segmentWaits(
  segment: ITourStep[],
  startIndex: number
): boolean[] {
  const firstAnchored = segment.findIndex(
    (step, i) => i >= startIndex && !!step.anchor
  );
  return segment.map(
    (_step, i) => i === 0 || i === startIndex || i === firstAnchored
  );
}

/** The contiguous run of steps on `route` around `index` — what one driver instance drives. */
export function segmentAround(
  visible: ITourStep[],
  index: number
): { start: number; end: number } {
  const { route } = visible[index];
  let start = index;
  while (start > 0 && visible[start - 1].route === route) start -= 1;
  let end = index;
  while (end < visible.length - 1 && visible[end + 1].route === route) {
    end += 1;
  }
  return { start, end };
}

/** What this run has done with its steps so far, for the counter (the saved progress's lists). */
export interface IStepsSeen {
  /** Steps the trainee moved on from. */
  shown?: Iterable<string>;
  /** Steps the engine skipped: an optional step that did not appear, a target that was missing. */
  skipped?: Iterable<string>;
}

/**
 * "Module 2 · step 7 of 16": the step's position in its module — the module it is counted in (`countIn`,
 * else `module`), so a step run inside another module counts there.
 *
 * Counted: the steps this run shows. A step the engine skipped is left out, and so is an `optional` step
 * (a duplicate warning, an eligibility message) until it shows, so the numbers never jump (John's review:
 * "Module 2 · step 6" went straight to "step 8" over a skipped duplicate warning). A step shown before
 * stays counted, even if it was skipped on an earlier run. So the total can grow by one when an optional
 * step does appear, and shrink when a step ahead turns out to be missing; the index never skips.
 */
export function stepPosition(
  visible: ITourStep[],
  stepId: string,
  seen: IStepsSeen = {}
): { module: TourModule; index: number; total: number } | null {
  const step = visible.find((s) => s.id === stepId);
  if (!step) return null;
  const shown = new Set(seen.shown ?? []);
  const skipped = new Set(seen.skipped ?? []);
  const counted = (s: ITourStep) =>
    s.id === stepId || shown.has(s.id) || (!skipped.has(s.id) && !s.optional);
  const countedIn = (s: ITourStep) => s.countIn ?? s.module;
  const module = countedIn(step);
  const inModule = visible.filter((s) => countedIn(s) === module && counted(s));
  return {
    module,
    index: inModule.findIndex((s) => s.id === stepId) + 1,
    total: inModule.length,
  };
}

/**
 * The URL a step's route opens on, or null when the engine cannot open it: the trainee's estimate before
 * it is known, and the patient page always (it needs the date-of-birth login the Collect Payment click
 * does, so it is entered only through that click).
 */
export function routeUrl(route: TourRoute, ctx: ITourContext): string | null {
  switch (route) {
    case "create":
      return `/orders/new?accountId=${ctx.accountId}`;
    case "estimate":
      return ctx.order?.id ? `/orders/${ctx.order.id}` : null;
    case "patient":
      return null;
    default:
      return `/hospitals/view/${ctx.accountId}/orders`;
  }
}
